/**
 * The newsletter, in three steps instead of one page — 17 Sep 2026.
 *
 * It used to stack everything at once: a product switch, a composer, a list of
 * sends and the suppression list, all down one column. Composing happened in a
 * box a third of the screen wide while the audience picker, the schedule and
 * the archive competed for attention beside it — so nothing had room and
 * nothing was obviously the next thing to do.
 *
 * Now there are three views and you are only ever in one:
 *
 *   **Log**      what has gone out and what is still a draft. The landing view.
 *   **Compose**  the message, full screen, and nothing else.
 *   **Send**     who it goes to, as what, and when.
 *
 * The split follows how the work actually goes: a newsletter is written once
 * and at length, and the audience is chosen in a minute at the end. Putting
 * them side by side gave equal room to unequal jobs, and gave the writing the
 * smaller half.
 *
 * ## The product switch is gone, and that is the point of the rework
 *
 * It used to pick both the audience and the sending identity at once — app or
 * pro, one or the other. But a message to the guides who also use the app is
 * one message, and under the old switch it was either two campaigns or a
 * decision about which half to leave out. Audience is now a multi-select across
 * both products, deduplicated by address on the server, and the sending
 * identity is its own choice on the Send step where it can be seen next to who
 * is receiving it.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAuth } from '@/features/auth/AuthProvider'
import { BlockEditor } from '@/shared/email/BlockEditor'
import { renderEmail } from '@/shared/email/renderEmail'
import { EMPTY_DOCUMENT, newBlock, starterBlocks, type EmailDocument } from '@/types/emailBlocks'
import { NewsletterLog } from './NewsletterLog'
import { NewsletterSend } from './NewsletterSend'
import {
  createDraft,
  isUnsent,
  loadNewsletter,
  updateDraft,
  type Newsletter,
} from './repository/newsletterRepository'
import './Newsletter.css'

type View = 'log' | 'compose' | 'send'

/**
 * The document a draft opens into.
 *
 * A newsletter written before sections existed has HTML and nothing else.
 * Dropping it into a single text section keeps every archived draft editable,
 * which is worth more than a migration — the old markup is exactly what a text
 * section holds.
 */
function documentFor(draft: Newsletter | null): EmailDocument {
  if (draft?.blocks?.length) return { ...EMPTY_DOCUMENT, blocks: draft.blocks }
  if (draft?.html) return { ...EMPTY_DOCUMENT, blocks: [{ ...newBlock('text'), html: draft.html }] }
  // A new one starts as a whole email rather than an empty page — see
  // `starterBlocks`.
  return { ...EMPTY_DOCUMENT, blocks: starterBlocks() }
}

export function NewsletterWorkspace() {
  const { user } = useAuth()
  const [view, setView] = useState<View>('log')

  /** The draft being worked on. Null on the log, set the moment one is opened. */
  const [draft, setDraft] = useState<Newsletter | null>(null)
  const [draftId, setDraftId] = useState('')
  const [name, setName] = useState('')
  const [subject, setSubject] = useState('')
  const [preheader, setPreheader] = useState('')
  const [doc, setDoc] = useState<EmailDocument>(() => documentFor(null))
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState('')
  const [error, setError] = useState<string | null>(null)

  // Derived, never typed into: the rendered message is what gets sent, hashed
  // and tested, and it must not be able to drift from the sections above it.
  const html = useMemo(() => renderEmail({ ...doc, preheader }), [doc, preheader])
  const fingerprint = `${name}|${subject}|${html}`

  const openDraft = useCallback((d: Newsletter | null) => {
    setDraft(d)
    setDraftId(d?.id ?? '')
    setName(d?.name ?? '')
    setSubject(d?.subject ?? '')
    setPreheader(d?.preheader ?? '')
    setDoc(documentFor(d))
    setSaved('')
    setError(null)
    setView('compose')
  }, [])

  // `?draft=<id>` opens that draft straight away — the automation canvas links
  // its Send email blocks here. Taken out of the address once read, so a reload
  // after leaving the editor lands on the log as usual.
  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get('draft')
    if (!id) return
    window.history.replaceState(null, '', window.location.pathname)
    loadNewsletter(id)
      .then((n) => {
        if (n && isUnsent(n)) openDraft(n)
        else if (!n) setError('That draft no longer exists.')
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)))
  }, [openDraft])

  const backToLog = () => {
    setView('log')
    setDraft(null)
    setDraftId('')
  }

  /**
   * Saving is explicit and idempotent.
   *
   * The first save creates the draft and remembers its id, so pressing it again
   * updates rather than filling the log with copies of the same unfinished
   * letter.
   */
  const save = async (): Promise<string> => {
    setSaving(true)
    setError(null)
    try {
      const input = {
        name: name.trim() || subject.trim() || 'Untitled',
        subject: subject.trim() || 'Untitled',
        preheader: preheader.trim(),
        html,
        blocks: doc.blocks,
        // The audience and kind belong to the send, not to the words. A draft
        // carries what it had; the Send step writes them when it sends.
        audience: draft?.audience ?? '',
        kind: (draft?.kind ?? 'marketing') as 'marketing' | 'service',
        sender: (draft?.sender ?? 'consumer') as 'consumer' | 'pro',
        savedBy: user?.email ?? '',
      }
      if (draftId) {
        await updateDraft(draftId, input)
        // A save is a draft again; see `updateDraft`.
        setDraft((d) => (d ? { ...d, status: 'draft', scheduledFor: null } : d))
        setSaved(fingerprint)
        return draftId
      }
      const id = await createDraft(input)
      setDraftId(id)
      setSaved(fingerprint)
      return id
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
      throw e
    } finally {
      setSaving(false)
    }
  }

  if (view === 'log') {
    return <NewsletterLog onCompose={() => openDraft(null)} onOpenDraft={openDraft} />
  }

  if (view === 'send') {
    return (
      <NewsletterSend
        subject={subject}
        html={html}
        blocks={doc.blocks}
        draftId={draftId}
        onBack={() => setView('compose')}
        onSent={backToLog}
      />
    )
  }

  return (
    <div className="nlw">
      <header className="nlw__bar">
        <button className="btn btn--ghost btn--tiny" onClick={backToLog}>
          ← All newsletters
        </button>

        <input
          className="input nlw__name"
          value={name}
          placeholder="Internal name"
          aria-label="Internal name — what the CRM calls this email; readers never see it"
          title="Internal name — what the CRM calls this email; readers never see it"
          onChange={(e) => setName(e.target.value)}
        />
        <input
          className="input nlw__subject"
          value={subject}
          placeholder="Subject line"
          aria-label="Subject line — what readers see"
          onChange={(e) => setSubject(e.target.value)}
        />
        <input
          className="input nlw__pre"
          value={preheader}
          placeholder="Preview text (optional)"
          aria-label="Preview text — the line an inbox shows after the subject"
          title="Preview text — the line an inbox shows after the subject"
          onChange={(e) => setPreheader(e.target.value)}
        />

        <div className="nlw__baracts">
          <span className="micro">
            {saving ? 'Saving…' : saved === fingerprint ? 'Draft saved' : 'Unsaved changes'}
          </span>
          <button className="btn btn--ghost" disabled={saving} onClick={() => void save()}>
            Save draft
          </button>
          <button
            className="btn btn--primary"
            disabled={saving || !subject.trim() || doc.blocks.length === 0}
            onClick={() => {
              // Saved on the way through, so nothing written is lost by moving
              // to the next step and changing one's mind there.
              void save()
                .then(() => setView('send'))
                .catch(() => undefined)
            }}
          >
            Audience and sending →
          </button>
        </div>
      </header>

      {error && <p className="recform__error nlw__error">{error}</p>}
      {draft?.status === 'scheduled' && draft.scheduledFor && (
        <p className="empty-note nlw__error">
          Scheduled for {draft.scheduledFor.toLocaleString('nb-NO', { dateStyle: 'medium', timeStyle: 'short' })}. Saving
          takes it off the schedule: send a new test and schedule it again under Audience and sending.
        </p>
      )}

      {/* Full width, full height. The editor and its preview are the screen. */}
      <div className="nlw__canvas">
        <BlockEditor value={doc} onChange={setDoc} subject={subject} />
      </div>
    </div>
  )
}
